import { motion } from 'framer-motion'
import { Handshake, DollarSign, TrendingUp, Award, ArrowRight, CheckCircle, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import SEO, { schemas } from '../components/SEO'

// Same order as benefits.items in partners.json.
const benefitIcons = [DollarSign, TrendingUp, Award]

interface PartnerType {
  title: string
  description: string
  features: string[]
}

export default function Partners() {
  const { t } = useTranslation(['partners', 'common'])
  const benefitTexts = t('benefits.items', { returnObjects: true }) as { title: string; description: string }[]
  const benefits = benefitIcons.map((icon, i) => ({ icon, ...benefitTexts[i] }))
  const partnerTypes = t('programs.items', { returnObjects: true }) as PartnerType[]
  const stats = t('stats', { returnObjects: true }) as { value: string; label: string }[]

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}  className="pt-32 pb-24">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/partners"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.partners'), url: '/partners' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Handshake className="w-4 h-4" /> {t('hero.badge')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">{t('hero.title')} <span className="text-gradient">{t('hero.titleAccent')}</span></h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto mb-8">
            {t('hero.text')}
          </p>
          <button className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all">
            {t('hero.apply')} <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-3 gap-6 mb-20">
          {stats.map((stat) => (
            <div key={stat.label} className="glass rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Benefits */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20">
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">{t('benefits.title')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={benefit.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="glass rounded-2xl p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Partner Types */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20">
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">{t('programs.title')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {partnerTypes.map((type, idx) => (
              <motion.div key={type.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="glass rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-4">{type.title}</h3>
                <p className="text-gray-400 mb-6">{type.description}</p>
                <ul className="space-y-3">
                  {type.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-success" /><span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-primary" /><span className="text-primary text-sm">{t('cta.badge')}</span>
          </div>
          <h2 className="text-3xl font-display font-bold text-white mb-6">{t('cta.title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('cta.text')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button className="px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all">{t('cta.apply')}</button>
            <a href="mailto:partners@cmdesk.help" className="px-8 py-4 glass hover:bg-white/10 text-white font-semibold rounded-xl transition-all">{t('cta.info')}</a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
